/**
 * Vertrieb (CRM) — Typen und Helfer (Paul 26.10.2026).
 */

export type Stufe = {
  schluessel: string
  label: string
  position: number
  art: "offen" | "gewonnen" | "verloren"
  farbe: string | null
  tage: number | null
}

export type Deal = {
  id: number
  titel: string
  ansprechpartner: string | null
  email: string | null
  telefon: string | null
  stufe: string
  wert_monat: number | null
  wert_einmalig: number | null
  zustaendig: string | null
  quelle: string | null
  abschluss_am: string | null
  naechster_schritt: string | null
  naechster_schritt_am: string | null
  verlust_grund: string | null
  kunde_slug: string | null
  notiz: string | null
  stufe_seit: string
  letzte_aktivitaet: string
  created_at: string
  updated_at: string
  offene_aufgaben: number
  naechste_aufgabe_am: string | null
  kontakt_id: number | null
  kontakt_firma?: string | null
}

export type Kontakt = {
  id: number
  name: string | null
  firma: string | null
  position: string | null
  email: string | null
  telefon: string | null
  website: string | null
  ort: string | null
  quelle: string | null
  zustaendig: string | null
  notiz: string | null
  created_at: string
  updated_at: string
  deals: number
  offen_wert_monat: number
  letzte_stufe: string | null
  letzte_aktivitaet: string | null
}

export const kontaktName = (k: Pick<Kontakt, "name" | "firma">) => (k.name || "").trim() || (k.firma || "").trim() || "Ohne Namen"

/** Ruhige Palette für Phasen — dieselben Töne wie die Standardphasen. */
export const PHASEN_FARBEN = ["#64748b", "#0ea5e9", "#6366f1", "#8b5cf6", "#a855f7", "#ec4899", "#f59e0b", "#f97316", "#10b981", "#14b8a6", "#ef4444", "#94a3b8"]

export type Erinnerung = {
  deal_id: number
  deal_titel: string
  stufe: string
  zustaendig: string | null
  art: "aufgabe" | "schritt" | "stillstand" | "abschluss" | "kein_schritt"
  text: string
  schwere: "rot" | "gelb" | "grau"
  aufgabe_id?: number
  faellig_am?: string
}

export type Aufgabe = {
  id: number
  deal_id: number
  deal_titel?: string
  titel: string
  faellig_am: string | null
  zustaendig: string | null
  erledigt_am: string | null
}

export type Aktivitaet = {
  id: number
  deal_id: number
  deal_titel?: string
  art: "notiz" | "anruf" | "mail" | "termin" | "stufe" | "angelegt" | "aufgabe"
  text: string | null
  meta: Record<string, string> | null
  von: string | null
  created_at: string
}

export type Vorlage = { schluessel: string; label: string; betreff: string; text: string }

export type Board = {
  stufen: Stufe[]
  deals: Deal[]
  erinnerungen: Erinnerung[]
  aufgaben: Aufgabe[]
  ich: string
  mail_bereit: boolean
  vorlagen: Vorlage[]
}

export async function crmFetch<T>(pfad: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { json, ...rest } = init || {}
  const r = await fetch(`/api/v1/crm${pfad}`, {
    credentials: "include",
    ...rest,
    headers: json !== undefined ? { "Content-Type": "application/json" } : rest.headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  })
  if (!r.ok) {
    let text = `Fehler ${r.status}`
    try {
      const d = await r.json()
      if (typeof d.detail === "string") text = d.detail
    } catch { /* kein JSON */ }
    throw new Error(text)
  }
  return r.json()
}

const EURO = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
export const euro = (n: number | null | undefined) => (n == null ? "" : EURO.format(n))

export const heuteIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

export const plusTage = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

const WT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]

/** „Heute", „Morgen", „Gestern", sonst „Mi 29.10." */
export function tagKurz(iso: string | null | undefined): string {
  if (!iso) return ""
  const tag = iso.slice(0, 10)
  const heute = heuteIso()
  if (tag === heute) return "Heute"
  if (tag === plusTage(heute, 1)) return "Morgen"
  if (tag === plusTage(heute, -1)) return "Gestern"
  const d = new Date(`${tag}T12:00:00`)
  return `${WT[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`
}

export function tageSeit(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000))
}

export function vorWann(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (min < 1) return "gerade eben"
  if (min < 60) return `vor ${min} Min.`
  const h = Math.floor(min / 60)
  if (h < 24) return `vor ${h} Std.`
  const t = Math.floor(h / 24)
  if (t === 1) return "gestern"
  if (t < 30) return `vor ${t} Tagen`
  return new Date(iso).toLocaleDateString("de-DE")
}

/** Ruhige, unterscheidbare Farbe je Person — fuer die Initialen. */
const PERSONEN_FARBEN = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#14b8a6", "#ef4444"]
export function personFarbe(name: string | null | undefined): string {
  const s = (name || "").toLowerCase()
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return PERSONEN_FARBEN[h % PERSONEN_FARBEN.length]
}

export function initialen(name: string | null | undefined): string {
  const teile = (name || "").trim().split(/[\s._-]+/).filter(Boolean)
  if (!teile.length) return "?"
  return (teile[0][0] + (teile[1]?.[0] || "")).toUpperCase()
}

/** Naechster Termin des Deals: frueheste offene Aufgabe oder „naechster Schritt am". */
export function naechsterTermin(d: Deal): string | null {
  const a = d.naechste_aufgabe_am, s = d.naechster_schritt_am
  if (a && s) return a < s ? a : s
  return a || s || null
}
